import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { getSettings } from "@/lib/api";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/companies", label: "Companies" },
  { to: "/leadership", label: "Leadership" },
  { to: "/governance", label: "Governance" },
  { to: "/careers", label: "Careers" },
  { to: "/news", label: "News" },
];

export const InstitutionalHeader = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: getSettings });

  const leadershipEnabled = !!settings?.leadershipEnabled;
  const nav = NAV.filter((n) => n.to !== "/leadership" || leadershipEnabled);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      className="sticky top-0 z-50 border-b transition-colors"
      style={{ borderColor: "var(--card-border)", background: scrolled ? "rgba(14,15,18,0.92)" : "var(--bg)", backdropFilter: "blur(8px)" }}
    >
      <a href="#main" data-testid="skip-to-content-link"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-[60] focus:rounded-md focus:px-3 focus:py-2"
        style={{ background: "var(--surface)", color: "var(--text)" }}>
        Skip to content
      </a>
      <div className="container-inst flex h-16 items-center justify-between">
        <Link to="/" data-testid="header-logo-link" className="flex items-center" aria-label="Acharya Holdings home">
          <img src="/brand/header-logo.webp" alt="Acharya Holdings" className="h-10 w-auto" />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase()}-link`}
              className="text-sm transition-colors"
              style={({ isActive }) => ({ color: isActive ? "var(--text)" : "var(--muted)" })}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Link to="/contact" data-testid="header-contact-button" className="btn btn-primary btn-sm">
            Contact
          </Link>
        </div>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button data-testid="header-mobile-menu-button" aria-label="Open menu"
              className="inline-flex h-10 w-10 items-center justify-center rounded-md border lg:hidden"
              style={{ borderColor: "var(--card-border)", color: "var(--text)" }}>
              <Menu size={20} />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[86%] max-w-sm border-l" style={{ background: "var(--bg)", borderColor: "var(--card-border)" }}>
            <div className="flex flex-col gap-1 pt-6">
              {nav.map((n) => (
                <SheetClose asChild key={n.to}>
                  <NavLink to={n.to} end={n.to === "/"}
                    data-testid={`mobile-nav-${n.label.toLowerCase()}-link`}
                    className="rounded-md px-3 py-3 text-base"
                    style={({ isActive }) => ({ color: isActive ? "var(--text)" : "var(--muted)", background: isActive ? "rgba(255,255,255,0.04)" : "transparent" })}>
                    {n.label}
                  </NavLink>
                </SheetClose>
              ))}
              <SheetClose asChild>
                <Link to="/contact" data-testid="mobile-nav-contact-link" className="btn btn-primary mt-3">
                  Contact
                </Link>
              </SheetClose>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};
